import React, { useState } from 'react';
import { Lock, Shield, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const SecuritySettings = ({ i18n }) => {
  const [settings, setSettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    passwordExpiry: 90,
    ipWhitelisting: false,
    maintenanceMode: false
  });

  const [blockedIPs, setBlockedIPs] = useState([
    { ip: '192.168.1.100', reason: 'Multiple failed login attempts', date: '2024-01-15' },
    { ip: '10.0.0.50', reason: 'Suspicious activity detected', date: '2024-01-14' }
  ]);

  const [newIP, setNewIP] = useState('');

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
    toast.success(
      i18n.language === 'ar' 
        ? 'تم تحديث الإعدادات' 
        : 'Settings updated'
    );
  };

  const handleBlockIP = () => {
    if (!newIP) {
      toast.error(i18n.language === 'ar' ? 'أدخل عنوان IP' : 'Enter IP address');
      return;
    }
    
    setBlockedIPs([...blockedIPs, {
      ip: newIP,
      reason: i18n.language === 'ar' ? 'محظور يدوياً' : 'Manually blocked',
      date: new Date().toISOString().split('T')[0]
    }]);
    setNewIP('');
    toast.success(i18n.language === 'ar' ? 'تم حظر IP' : 'IP blocked');
  };

  const handleUnblockIP = (ip) => {
    setBlockedIPs(blockedIPs.filter(item => item.ip !== ip));
    toast.success(i18n.language === 'ar' ? 'تم إلغاء الحظر' : 'IP unblocked');
  };

  const securityScore = () => {
    let score = 0;
    if (settings.twoFactorAuth) score += 20;
    if (settings.sessionTimeout <= 30) score += 20;
    if (settings.maxLoginAttempts <= 5) score += 20;
    if (settings.passwordExpiry <= 90) score += 20;
    if (settings.ipWhitelisting) score += 20;
    return score;
  };

  const score = securityScore();
  const scoreColor = score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = score >= 80 ? 'bg-green-100' : score >= 60 ? 'bg-amber-100' : 'bg-red-100';

  return (
    <div className="space-y-6">
      {/* Security Score */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-2">
              <Lock className="w-7 h-7 text-red-600" />
              {i18n.language === 'ar' ? 'الأمان' : 'Security Settings'}
            </h3>
            <p className="text-sm text-slate-500">
              {i18n.language === 'ar' ? 'إدارة إعدادات الأمان والحماية' : 'Manage security and protection settings'}
            </p>
          </div>
          <div className={`text-center ${scoreBg} rounded-xl p-4`}>
            <div className={`text-3xl font-bold ${scoreColor}`}>{score}%</div>
            <div className="text-xs text-slate-600 mt-1">
              {i18n.language === 'ar' ? 'درجة الأمان' : 'Security Score'}
            </div>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h4 className="text-lg font-bold text-slate-900 mb-4">
          {i18n.language === 'ar' ? 'إعدادات الأمان الأساسية' : 'Basic Security Settings'}
        </h4>
        
        <div className="space-y-4">
          {/* Two-Factor Auth */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-purple-600" />
              <div>
                <div className="font-semibold text-slate-900">
                  {i18n.language === 'ar' ? 'المصادقة الثنائية' : 'Two-Factor Authentication'}
                </div>
                <div className="text-sm text-slate-500">
                  {i18n.language === 'ar' ? 'أمان إضافي لحسابات المستخدمين' : 'Extra security for user accounts'}
                </div>
              </div>
            </div>
            <Switch
              checked={settings.twoFactorAuth}
              onCheckedChange={() => handleToggle('twoFactorAuth')}
            />
          </div>

          {/* Session Timeout */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="mb-2 block font-semibold">
              {i18n.language === 'ar' ? 'مهلة الجلسة (بالدقائق)' : 'Session Timeout (minutes)'}
            </Label>
            <Input
              type="number"
              value={settings.sessionTimeout}
              onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
              min="5"
              max="1440"
            />
            <p className="text-xs text-slate-500 mt-2">
              {i18n.language === 'ar' ? 'المدة قبل انتهاء الجلسة تلقائياً' : 'Time before session expires automatically'}
            </p>
          </div>

          {/* Max Login Attempts */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="mb-2 block font-semibold">
              {i18n.language === 'ar' ? 'عدد محاولات تسجيل الدخول' : 'Max Login Attempts'}
            </Label>
            <Input
              type="number"
              value={settings.maxLoginAttempts}
              onChange={(e) => setSettings({ ...settings, maxLoginAttempts: parseInt(e.target.value) })}
              min="3"
              max="10"
            />
            <p className="text-xs text-slate-500 mt-2">
              {i18n.language === 'ar' ? 'عدد المحاولات الفاشلة قبل قفل الحساب' : 'Failed attempts before account lockout'}
            </p>
          </div>

          {/* Password Expiry */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="mb-2 block font-semibold">
              {i18n.language === 'ar' ? 'انتهاء صلاحية كلمة المرور (بالأيام)' : 'Password Expiry (days)'}
            </Label>
            <Input
              type="number"
              value={settings.passwordExpiry}
              onChange={(e) => setSettings({ ...settings, passwordExpiry: parseInt(e.target.value) })}
              min="30"
              max="365"
            />
            <p className="text-xs text-slate-500 mt-2">
              {i18n.language === 'ar' ? 'عدد الأيام قبل طلب تغيير كلمة المرور' : 'Days before password change is required'}
            </p>
          </div>

          {/* IP Whitelisting */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <div className="font-semibold text-slate-900">
                  {i18n.language === 'ar' ? 'قائمة IP البيضاء' : 'IP Whitelisting'}
                </div>
                <div className="text-sm text-slate-500">
                  {i18n.language === 'ar' ? 'السماح فقط لعناوين IP محددة' : 'Only allow specific IP addresses'}
                </div>
              </div>
            </div>
            <Switch
              checked={settings.ipWhitelisting}
              onCheckedChange={() => handleToggle('ipWhitelisting')}
            />
          </div>

          {/* Maintenance Mode */}
          <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <div className="font-semibold text-slate-900">
                  {i18n.language === 'ar' ? 'وضع الصيانة' : 'Maintenance Mode'}
                </div>
                <div className="text-sm text-slate-500">
                  {i18n.language === 'ar' ? 'إيقاف الموقع مؤقتاً للصيانة' : 'Temporarily disable site for maintenance'}
                </div>
              </div>
            </div>
            <Switch
              checked={settings.maintenanceMode}
              onCheckedChange={() => handleToggle('maintenanceMode')}
            />
          </div>
        </div>
      </div>

      {/* Blocked IPs */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h4 className="text-lg font-bold text-slate-900 mb-4">
          {i18n.language === 'ar' ? 'عناوين IP المحظورة' : 'Blocked IP Addresses'}
        </h4>

        {/* Add New IP */}
        <div className="flex gap-2 mb-4">
          <Input
            placeholder={i18n.language === 'ar' ? 'أدخل عنوان IP...' : 'Enter IP address...'}
            value={newIP}
            onChange={(e) => setNewIP(e.target.value)}
          />
          <Button onClick={handleBlockIP} variant="outline">
            {i18n.language === 'ar' ? 'حظر' : 'Block'}
          </Button>
        </div>

        <div className="space-y-3">
          {blockedIPs.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
              <div>
                <div className="font-mono font-semibold text-slate-900">{item.ip}</div>
                <div className="text-sm text-slate-600">{item.reason}</div>
                <div className="text-xs text-slate-500 mt-1">{item.date}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUnblockIP(item.ip)}
                className="text-red-600 hover:bg-red-100"
              >
                <XCircle className="w-4 h-4 me-1" />
                {i18n.language === 'ar' ? 'إلغاء الحظر' : 'Unblock'}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;